import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import commons from '@medflex/commons';
import Button from '@medflex/components/stories/Button';
import { IconCheckmarkOutline } from '@medflex/components/stories/Icons';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import { Switch } from '@medflex/components/stories/Switch';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import config from '@medflex/utils/config';
import saveToClipboard from '@medflex/utils/saveToClipboard';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';

import Link from '@medflex/components/stories/Link';
import { MODALS } from '../../hooks/useIsModal';
import useModal from '../../hooks/useModal';
import useUserProfile from '../../hooks/useUserProfile';
import useCountries from '../../hooks/useCountries';

const PhysicianPagesContactModule = () => {
  const { userProfile, editContactPage, isLoadingUserProfile, isUpdatingContactPage } =
    useUserProfile();
  const pageLinkRef = useRef(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState();
  const { t } = useTranslation('physician');
  const { open } = useModal();
  const { contactPage: errors = {} } = useSelector(state => state.errors);

  const countries = useCountries();

  const { slugWithId } = userProfile.attributes;
  const pagesLink = `${config.PHYSICIAN_PAGES_URL}/${slugWithId}`;

  const isIndexed = useMemo(
    () =>
      userProfile &&
      userProfile.attributes.pages &&
      userProfile.attributes.pages.metaRobotsContent === 'index',
    [userProfile],
  );

  const { address, address2, postalCode, city, country } = useMemo(() => {
    if (userProfile) {
      const { attributes } = userProfile;

      return {
        ...attributes,
        country: attributes.countryCode ? countries[attributes.countryCode] : null,
      };
    }
    return null;
  }, [countries, userProfile]);

  const personalNote = useMemo(
    () =>
      (userProfile && userProfile.attributes.pages && userProfile.attributes.pages.personalNote) ||
      commons.constant.contactPage.personalNote.getText(),
    [userProfile],
  );

  const openingHours = useMemo(
    () => userProfile && userProfile.attributes.pages && userProfile.attributes.pages.openingHours,
    [userProfile],
  );

  const services = useMemo(
    () => userProfile && userProfile.attributes.pages && userProfile.attributes.pages.services,
    [userProfile],
  );

  useEffect(() => {
    const { update: error } = errors;
    if (isSubmitted && !isUpdatingContactPage) {
      if (error) {
        showAlertToast(
          t('toasts.contact_page.failure.title'),
          t('toasts.contact_page.failure.text'),
          {
            toastId: 'contactPageFailed',
          },
        );
        return;
      }
      showSuccessToast(
        t('toasts.contact_page.success.title'),
        t('toasts.contact_page.success.text', {
          allowHtml: true,
          url: pagesLink || '',
        }),
        {
          toastId: 'contactPageSuccess',
        },
      );
      setIsSubmitted(false);
    }
  }, [errors, isSubmitted, isUpdatingContactPage, pagesLink, t]);

  const copyLink = () => {
    saveToClipboard(pageLinkRef.current);
    setIsCopied(true);
    showSuccessToast(t('toasts.permanent_link.copied'), null, {
      toastId: 'permanentLinkCopySuccess',
    });
  };

  const handleCheckIndex = ({ shouldIndex }) => {
    if (!isSubmitted) {
      const { pages } = userProfile.attributes;
      const previousMetaRobotsContent = userProfile && pages.metaRobotsContent;
      const newMetaRobotsContent = shouldIndex ? 'index' : 'noindex';
      editContactPage({
        metaRobotsContent: newMetaRobotsContent,
      });
      if (previousMetaRobotsContent !== newMetaRobotsContent) {
        trackEvent({
          zone: 'PhysicianPagesLinkModal_META',
          action: TRACKING_ACTIONS.SUBMIT,
          elementType: TRACKING_ELEMENT_TYPES.FORM,
          actionText: shouldIndex ? 'google_index_select' : 'google_index_remove',
        });
      }
      setIsSubmitted(true);
    }
  };

  if (isLoadingUserProfile) {
    return null;
  }

  return (
    <div data-component="PhysicianPagesContactModule" className="flex flex-col">
      <header className="mb-6 flex items-center">
        <h2 className="text-2xl font-semibold">{t('PhysicianPagesContactModule.title')}</h2>
      </header>
      <p className="mb-6 text-slate-600">
        <Trans i18nKey="physician:PhysicianPagesContactModule.description" t={t}>
          <a
            href={pagesLink || ''}
            target="_blank"
            className="hover:underline text-blue-500"
            rel="noreferrer"
          >
            {t('PhysicianPagesContactModule.short_title')}
          </a>
        </Trans>
      </p>
      <div className="mb-6 flex flex-col items-center lg:flex-row">
        <div className="mb-4 h-12 w-full truncate rounded border border-slate-300 bg-slate-100 p-2 lg:mb-0">
          <a
            href={pagesLink}
            target="_blank"
            className="select-all break-all text-slate-500 hover:underline"
            ref={pageLinkRef}
            rel="noreferrer noopener"
          >
            {pagesLink}
          </a>
        </div>
        <Button
          isActive={isCopied}
          kind="primary:outline"
          className="flex-shrink-0 lg:ml-4 lg:w-auto"
          onClick={copyLink}
        >
          {isCopied && <IconCheckmarkOutline className="mr-2" />}
          {isCopied
            ? t('PhysicianPagesContactModule.url.copied')
            : t('PhysicianPagesContactModule.url.copy')}
        </Button>
      </div>
      <SettingsBox className="mb-12" title={t('PhysicianPagesContactModule.visibility.title')}>
        <label
          className="flex justify-between"
          htmlFor="PhysicianPagesContactModule_isIndexedSwitch"
        >
          <div className="mb-2 font-semibold">
            {t('PhysicianPagesContactModule.visibility.toggle')}
          </div>
          <Switch
            id="PhysicianPagesContactModule_isIndexedSwitch"
            checked={isIndexed}
            disabled={isUpdatingContactPage}
            label={t('PhysicianPagesContactModule.visibility.toggle', {
              allowHtml: true,
            })}
            onChange={value => {
              handleCheckIndex({ shouldIndex: value });
            }}
          />
        </label>
        <p className="whitespace-pre-line">
          {t('PhysicianPagesContactModule.visibility.description')}
        </p>
      </SettingsBox>

      <div className="relative mb-12">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-slate-200" />
        </div>
      </div>

      <h3 className="mb-6 text-xl font-semibold">
        {t('PhysicianPagesContactModule.contentConfigurationTitle')}
      </h3>

      <SettingsBox
        className="mb-6"
        title={t('PhysicianPagesContactModule.personal_note.title')}
        canEdit
        onEdit={() => open({ modal: MODALS.pages })}
      >
        <p className="truncate">{personalNote}</p>
      </SettingsBox>

      <SettingsBox
        className="mb-6"
        title={t('PhysicianPagesContactModule.opening_hours.title')}
        canEdit
        onEdit={() => open({ modal: MODALS.openingHours })}
      >
        <div className="space-y-4">
          {openingHours?.slots &&
          Object.values(openingHours.slots).some(slot => Boolean(slot.length)) ? (
            <table>
              <tbody className="space-y-2">
                {Object.entries(openingHours.slots).map(
                  ([day, slots]) =>
                    Boolean(slots.length) && (
                      <tr className="flex flex-col lg:table-row">
                        <th className="text-left">
                          {t(`PhysicianPagesContactModule.opening_hours.${day}`)}
                        </th>
                        {slots.map(slot => (
                          <td className="max-w-[30ch] truncate lg:pl-4 tabular-nums">{slot}</td>
                        ))}
                      </tr>
                    ),
                )}
              </tbody>
            </table>
          ) : (
            <div className="italic text-slate-600">
              {t('PhysicianPagesContactModule.opening_hours.no_opening_hours')}
            </div>
          )}
          <div>
            <div className="text-sm text-slate-600">
              {t('PhysicianPagesContactModule.opening_hours.additional_information')}
            </div>
            <p className="truncate">{openingHours?.additionalInformation || '-'}</p>
          </div>
        </div>
      </SettingsBox>

      <SettingsBox
        className="mb-6"
        title={t('PhysicianPagesContactModule.address.title')}
        canEdit
        onEdit={() => open({ modal: MODALS.address })}
      >
        <div className="space-y-2">
          <div>
            <div className="text-sm text-slate-600">
              {t('PhysicianPagesContactModule.address.streetNumber')}
            </div>
            <div>
              {address || '-'} {address2}
            </div>
          </div>
          <div className="flex space-x-4">
            <div>
              <div className="text-sm text-slate-600">
                {t('PhysicianPagesContactModule.address.postalCode')}
              </div>
              <div>{postalCode || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-slate-600">
                {t('PhysicianPagesContactModule.address.city')}
              </div>
              <div>{city || '-'}</div>
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-600">
              {t('PhysicianPagesContactModule.address.country')}
            </div>
            <div>{country || '-'}</div>
          </div>
        </div>
      </SettingsBox>

      <SettingsBox
        className="mb-6"
        title={t('PhysicianPagesContactModule.services.title')}
        canEdit
        onEdit={() => open({ modal: MODALS.services })}
      >
        <div className="truncate">
          {services?.length ? (
            services.map((service, index) => (index ? ', ' : '') + service)
          ) : (
            <div className="italic text-slate-600">
              {t('PhysicianPagesContactModule.services.no_services')}
            </div>
          )}
        </div>
      </SettingsBox>

      <SettingsBox title={t('PhysicianPagesContactModule.inquiryManagement.title')}>
        <div className="mb-3">{t('PhysicianPagesContactModule.inquiryManagement.description')}</div>
        <Link kind="button:outline" to="/settings/inquiryManagement">
          {t('PhysicianPagesContactModule.inquiryManagement.link')}
        </Link>
      </SettingsBox>
    </div>
  );
};

export default PhysicianPagesContactModule;
